/**
 * Contrato de error de la API y su excepción tipada.
 *
 * Todos los errores de `/api/v1` siguen el mismo contrato JSON del backend
 * (ver `application/api/errors.py`): `{ error_code, message, details? }`. El
 * cliente lo traduce a una `ApiError` para que hooks y pantallas la traten de
 * forma uniforme, en vez de inspeccionar respuestas crudas de `ky`.
 */

/** Forma del cuerpo de error que emite el backend. */
export interface ApiErrorBody {
  error_code: string;
  message: string;
  details?: unknown;
}

/** Indica si un valor desconocido cumple el contrato `{error_code, message}`. */
export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.error_code === "string" &&
    typeof candidate.message === "string"
  );
}

/** Excepción tipada para cualquier respuesta de error de la API. */
export class ApiError extends Error {
  readonly error_code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(params: {
    error_code: string;
    message: string;
    status: number;
    details?: unknown;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.error_code = params.error_code;
    this.status = params.status;
    this.details = params.details;
    // Mantiene `instanceof ApiError` correcto al transpilar a ES5/ES2020.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}
